import type { ReactNode } from "react";
import { cn } from "@repo/utils";
import { DataTableBody } from "./data-table-body.js";
import { DataTableHeader } from "./data-table-header.js";
import { DataTableToolbar } from "./data-table-toolbar.js";
import type { ColumnDef, RowClassNameType, RowType } from "./data-table.types.js";
import { useDataTableFiltering } from "./hooks/use-data-table-filtering.js";
import { useDataTablePagination } from "./hooks/use-data-table-pagination.js";
import { useDataTableSorting } from "./hooks/use-data-table-sorting.js";
import { useDataTableState } from "./hooks/use-data-table-state.js";
import { Pagination } from "./pagination/pagination.js";
import { DataTableState } from "./states/data-table-state.js";
import type { ItemProps } from "../dropdown-menu/dropdown-menu-item-list.js";
import { defaultGetRowId } from "./utils/data-table-utils.js";

export type RowBase = Record<string, unknown> & { id?: string | number; subRows?: unknown[] };

export type ActionContext<T extends RowBase> = {
  row: T;
  index: number;
};

type Props<TRow extends RowBase, TActionContext = ActionContext<TRow>> = {
  data: TRow[];
  columns: ColumnDef<TRow>[];
  actions?: ItemProps<TActionContext>[];
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  rowClassName?: RowClassNameType<TRow>;
  onRowClick?: RowType<TRow>;
  toolbarActions?: ReactNode;
  renderColumnFilters?: (
    toggleFilter: ReturnType<typeof useDataTableFiltering>["toggleFilter"],
    setColumnFilter: ReturnType<typeof useDataTableFiltering>["setColumnFilter"],
    filters: ReturnType<typeof useDataTableFiltering>["filters"]
  ) => ReactNode;
  pagination?: {
    enabled: boolean;
    pageSize?: number;
    pageSizeOptions?: number[];
    showPageSizeSelector?: boolean;
    showResultsInfo?: boolean;
  };
  isLoading?: boolean;
  isError?: boolean;
  emptyStateMessage?: string;
  errorMessage?: string;
  getRowId?: (row: TRow) => string | number;
};

export const DataTable = <TRow extends RowBase, TActionContext = ActionContext<TRow>>({
  data,
  columns,
  actions,
  className,
  headerClassName,
  bodyClassName,
  rowClassName,
  onRowClick,
  toolbarActions,
  renderColumnFilters,
  pagination = { enabled: false, pageSize: 10, pageSizeOptions: [10, 20, 50, 100] },
  isLoading = false,
  isError = false,
  emptyStateMessage,
  errorMessage,
  getRowId = defaultGetRowId,
}: Props<TRow, TActionContext>) => {
  const { searchTerm, setSearchTerm, filters, toggleFilter, setColumnFilter, filteredData } =
    useDataTableFiltering(data, columns);

  const { sortConfig, handleSort, sortedData } = useDataTableSorting(filteredData);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    totalPages,
    paginatedData,
  } = useDataTablePagination(sortedData, pagination);

  const { isSearchExpanded, setIsSearchExpanded } = useDataTableState();

  const hasSearchableColumns = columns.some((col) => col.searchable);
  const hasActions = !!(actions && actions.length > 0);
  const showActionsColumn =
    hasSearchableColumns || hasActions || !!toolbarActions || !!renderColumnFilters;

  const actionsColumnContent = (
    <>
      {toolbarActions && (
        <div className="invisible flex flex-shrink-0 items-center gap-2">{toolbarActions}</div>
      )}
      {renderColumnFilters && true && (
        <div className="invisible flex flex-shrink-0 items-center gap-2">
          {renderColumnFilters(toggleFilter, setColumnFilter, filters)}
        </div>
      )}
    </>
  );

  const hasData = paginatedData.length > 0;

  return (
    <div className={cn("w-full space-y-4 overflow-x-auto", className)}>
      <table className="relative min-w-full table-auto">
        <DataTableHeader
          columns={columns}
          sortConfig={sortConfig}
          onSort={handleSort}
          headerClassName={headerClassName}
          showActionsColumn={showActionsColumn}
          actionsHeaderContent={
            <DataTableToolbar
              toolbarActions={toolbarActions}
              renderColumnFilters={renderColumnFilters?.(toggleFilter, setColumnFilter, filters)}
              hasSearchableColumns={hasSearchableColumns}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              isSearchExpanded={isSearchExpanded}
              onSearchExpandedChange={setIsSearchExpanded}
            />
          }
          hasActions={hasActions}
          hasSearchableColumns={hasSearchableColumns}
        />

        {isLoading || isError || !hasData ? (
          <DataTableState
            variant={isLoading ? "loading" : isError ? "error" : !hasData ? "empty" : "loading"}
            columns={columns}
            message={isError ? errorMessage : emptyStateMessage}
            showActionsColumn={showActionsColumn}
          />
        ) : (
          <DataTableBody<TRow, TActionContext>
            data={paginatedData}
            columns={columns}
            actions={actions}
            bodyClassName={bodyClassName}
            rowClassName={rowClassName}
            onRowClick={onRowClick}
            showActionsColumn={showActionsColumn}
            actionsColumnContent={actionsColumnContent}
            hasSearchableColumns={hasSearchableColumns}
            hasActions={hasActions}
            isSearchExpanded={isSearchExpanded}
            getRowId={getRowId}
          />
        )}
      </table>

      {pagination.enabled && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={pagination.pageSizeOptions}
          showPageSizeSelector={pagination.showPageSizeSelector}
          showResultsInfo={pagination.showResultsInfo}
          className="px-4"
        />
      )}
    </div>
  );
};
